import { Injectable } from '@angular/core';
import { RestApiClientService } from '@core/rest-api-client/rest-api-client.service';
import { fetchOccupationListByOnetCodesQuery } from '@pages/jobs/utils/jobs-detail.util';
import { EntityType } from '@utils/enum/entity-type.enum';
import { HttpRequestMethod } from '@utils/enum/http-request-method.enum';
import { Apollo } from 'apollo-angular';
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { IUser } from 'src/gen';
import { generateUserAttributes } from './utils/generate-user-attributes.util';

export interface AuthResponse {
  jwt: string;
}

export interface UserInfoResponse {
  user: IUser;
  feedbackSurveyPassed: number;
}

export interface UpdateUserResponse {
  user: IUser;
}

export interface OccupationsResponse {
  occupations: any[];
}

export interface ForgotPasswordResponse {
  message: string;
}

export interface ResetPasswordResponse {
  message: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  constructor(private readonly apiClient: RestApiClientService, private readonly apollo: Apollo) {}

  register(username: string, password: string): Observable<AuthResponse> {
    return this.apiClient
      .fetchEntityCollection({
        entityType: `${EntityType.Auth}/local/register`,
        data: { username, email: username, password },
        method: HttpRequestMethod.Post
      })
      .pipe(map(response => ({ jwt: response.jwt })));
  }

  login(identifier: string, password: string): Observable<AuthResponse> {
    return this.apiClient
      .fetchEntityCollection({
        entityType: `${EntityType.Auth}/local`,
        data: { identifier, password },
        method: HttpRequestMethod.Post
      })
      .pipe(map(response => ({ jwt: response.jwt })));
  }

  forgotPassword(email: string): Observable<ForgotPasswordResponse> {
    return this.apiClient
      .fetchEntityCollection({
        entityType: `${EntityType.Auth}/forgot-password`,
        data: { email },
        method: HttpRequestMethod.Post
      })
      .pipe(map(response => ({ message: 'Password reset email sent successfully' })));
  }

  resetPassword(code: string, password: string, passwordConfirmation: string): Observable<ResetPasswordResponse> {
    return this.apiClient
      .fetchEntityCollection({
        entityType: `${EntityType.Auth}/reset-password`,
        data: { code, password, passwordConfirmation },
        method: HttpRequestMethod.Post
      })
      .pipe(map(response => ({ message: 'Password reset successfully' })));
  }

  fetchUserInfo(): Observable<UserInfoResponse> {
    return this.apiClient
      .fetchEntityCollection({
        entityType: `${EntityType.User}/me`,
        populate: generateUserAttributes()
      })
      .pipe(
        switchMap(user =>
          this.apiClient
            .fetchEntityCollectionWithMeta({
              entityType: `feedback-surveys`,
              queryParams: { '[user][id][$eq]': user.id }
            })
            .pipe(
              map(data => ({
                user,
                feedbackSurveyPassed: data.meta.pagination.total
              }))
            )
        )
      );
  }

  updateUserInfo(id: string, data: any): Observable<UpdateUserResponse> {
    return this.apiClient
      .createOrUpdateEntity({
        entityType: `${EntityType.User}`,
        entityID: id,
        data
      })
      .pipe(
        switchMap(() =>
          this.apiClient.fetchEntityCollection({
            entityType: `${EntityType.User}/me`,
            populate: generateUserAttributes()
          })
        ),
        map(response => ({ user: response }))
      );
  }

  fetchOccupations(onetCodes: string[]): Observable<OccupationsResponse> {
    if (!onetCodes || onetCodes.length === 0) {
      return new Observable(subscriber => {
        subscriber.next({ occupations: [] });
        subscriber.complete();
      });
    }

    const query = fetchOccupationListByOnetCodesQuery();
    const uniqueOnetCodes = Array.from(new Set(onetCodes?.filter(code => !!code)));
    return this.apollo
      .use('cpb')
      .query<any>({
        query,
        variables: { onetCodes: uniqueOnetCodes }
      })
      .pipe(
        map(response => ({
          occupations: response.data.occupationsOrCustomByOnetCodes
        }))
      );
  }
}
